/**
 * Minimal regex-based syntax highlighter for TypeScript / JavaScript / JSON / CSS / Bash / HTML.
 *
 * Zero dependencies. Not semantically correct (it's regex) but good enough for
 * Storybook demos and in-app code samples. Token classes map to .syntax-*
 * rules in theme.css so highlighting follows the theme automatically.
 *
 * If we ever need real tokenization (quoted strings with escapes, nested
 * template literals, etc.) swap in `shiki` or `prism-react-renderer` — the
 * call-site API stays the same.
 */

type Token = {
  type:
    | 'keyword'
    | 'string'
    | 'number'
    | 'comment'
    | 'function'
    | 'property'
    | 'tag'
    | 'punct'
    | 'text'
  value: string
}

const KEYWORDS_JS = new Set([
  'import',
  'export',
  'from',
  'as',
  'default',
  'const',
  'let',
  'var',
  'function',
  'return',
  'if',
  'else',
  'for',
  'while',
  'do',
  'switch',
  'case',
  'break',
  'continue',
  'class',
  'extends',
  'implements',
  'interface',
  'type',
  'enum',
  'public',
  'private',
  'protected',
  'readonly',
  'static',
  'new',
  'this',
  'super',
  'null',
  'undefined',
  'true',
  'false',
  'async',
  'await',
  'try',
  'catch',
  'finally',
  'throw',
  'typeof',
  'instanceof',
  'in',
  'of',
  'void',
  'delete',
])

const KEYWORDS_BASH = new Set([
  'cd',
  'ls',
  'echo',
  'export',
  'function',
  'if',
  'then',
  'else',
  'fi',
  'for',
  'in',
  'do',
  'done',
  'while',
  'return',
  'source',
])

export type Language =
  | 'ts'
  | 'tsx'
  | 'js'
  | 'jsx'
  | 'json'
  | 'css'
  | 'bash'
  | 'sh'
  | 'html'
  | 'text'

export function tokenize(code: string, lang: Language = 'ts'): Token[] {
  if (lang === 'json') return tokenizeJson(code)
  if (lang === 'css') return tokenizeCss(code)
  if (lang === 'bash' || lang === 'sh') return tokenizeBash(code)
  if (lang === 'html') return tokenizeHtml(code)
  return tokenizeJs(code)
}

/** JS/TS/TSX/JSX */
function tokenizeJs(code: string): Token[] {
  const tokens: Token[] = []
  // Order matters: comments → strings → keywords/identifiers → numbers → punct
  const regex = new RegExp(
    [
      '(\\/\\*[\\s\\S]*?\\*\\/)', // 1 block comment
      '(\\/\\/[^\\n]*)', // 2 line comment
      '(`(?:\\\\[\\s\\S]|[^`\\\\])*`)', // 3 template string
      '("(?:\\\\[\\s\\S]|[^"\\\\])*")', // 4 double-quoted string
      "('(?:\\\\[\\s\\S]|[^'\\\\])*')", // 5 single-quoted string
      '(\\b0[xX][0-9a-fA-F]+\\b|\\b\\d+(?:\\.\\d+)?\\b)', // 6 number
      '(\\b[A-Za-z_$][\\w$]*\\b)', // 7 identifier/keyword
      '([{}()\\[\\].,;:<>=+\\-*/%!&|^~?]+)', // 8 punct
    ].join('|'),
    'g',
  )
  let last = 0
  for (const m of code.matchAll(regex)) {
    const idx = m.index ?? 0
    if (idx > last) tokens.push({ type: 'text', value: code.slice(last, idx) })
    if (m[1] || m[2]) tokens.push({ type: 'comment', value: m[0] })
    else if (m[3] || m[4] || m[5]) tokens.push({ type: 'string', value: m[0] })
    else if (m[6]) tokens.push({ type: 'number', value: m[0] })
    else if (m[7]) {
      const word = m[7]
      if (KEYWORDS_JS.has(word)) tokens.push({ type: 'keyword', value: word })
      else if (/^[A-Z]/.test(word))
        tokens.push({ type: 'tag', value: word }) // Component / Class
      else if (code[idx + word.length] === '(')
        tokens.push({ type: 'function', value: word }) // fn call
      else tokens.push({ type: 'text', value: word })
    } else if (m[8]) tokens.push({ type: 'punct', value: m[0] })
    last = idx + m[0].length
  }
  if (last < code.length) tokens.push({ type: 'text', value: code.slice(last) })
  return tokens
}

/** JSON */
function tokenizeJson(code: string): Token[] {
  const tokens: Token[] = []
  const regex =
    /("(?:\\[\s\S]|[^"\\])*"\s*:?)|("(?:\\[\s\S]|[^"\\])*")|(\b\d+(?:\.\d+)?\b)|(\btrue\b|\bfalse\b|\bnull\b)|([{}[\],])/g
  let last = 0
  for (const m of code.matchAll(regex)) {
    const idx = m.index ?? 0
    if (idx > last) tokens.push({ type: 'text', value: code.slice(last, idx) })
    if (m[1]) tokens.push({ type: 'property', value: m[0] })
    else if (m[2]) tokens.push({ type: 'string', value: m[0] })
    else if (m[3]) tokens.push({ type: 'number', value: m[0] })
    else if (m[4]) tokens.push({ type: 'keyword', value: m[0] })
    else if (m[5]) tokens.push({ type: 'punct', value: m[0] })
    last = idx + m[0].length
  }
  if (last < code.length) tokens.push({ type: 'text', value: code.slice(last) })
  return tokens
}

/** CSS */
function tokenizeCss(code: string): Token[] {
  const tokens: Token[] = []
  const regex =
    /(\/\*[\s\S]*?\*\/)|(@[\w-]+)|("(?:\\[\s\S]|[^"\\])*"|'(?:\\[\s\S]|[^'\\])*')|(#[0-9a-fA-F]{3,8}\b)|([.#][\w-]+)|(\b[\w-]+)(?=\s*:)|([{}();,:])/g
  let last = 0
  for (const m of code.matchAll(regex)) {
    const idx = m.index ?? 0
    if (idx > last) tokens.push({ type: 'text', value: code.slice(last, idx) })
    if (m[1]) tokens.push({ type: 'comment', value: m[0] })
    else if (m[2]) tokens.push({ type: 'keyword', value: m[0] })
    else if (m[3]) tokens.push({ type: 'string', value: m[0] })
    else if (m[4]) tokens.push({ type: 'number', value: m[0] })
    else if (m[5]) tokens.push({ type: 'function', value: m[0] })
    else if (m[6]) tokens.push({ type: 'property', value: m[0] })
    else if (m[7]) tokens.push({ type: 'punct', value: m[0] })
    last = idx + m[0].length
  }
  if (last < code.length) tokens.push({ type: 'text', value: code.slice(last) })
  return tokens
}

/** Bash */
function tokenizeBash(code: string): Token[] {
  const tokens: Token[] = []
  const regex =
    /(#[^\n]*)|("(?:\\[\s\S]|[^"\\])*"|'(?:\\[\s\S]|[^'\\])*')|(\$\w+|\$\{[^}]+\})|(\b\w+\b)|([|&;<>()])/g
  let last = 0
  for (const m of code.matchAll(regex)) {
    const idx = m.index ?? 0
    if (idx > last) tokens.push({ type: 'text', value: code.slice(last, idx) })
    if (m[1]) tokens.push({ type: 'comment', value: m[0] })
    else if (m[2]) tokens.push({ type: 'string', value: m[0] })
    else if (m[3]) tokens.push({ type: 'property', value: m[0] })
    else if (m[4]) tokens.push({ type: KEYWORDS_BASH.has(m[0]) ? 'keyword' : 'text', value: m[0] })
    else if (m[5]) tokens.push({ type: 'punct', value: m[0] })
    last = idx + m[0].length
  }
  if (last < code.length) tokens.push({ type: 'text', value: code.slice(last) })
  return tokens
}

/** HTML */
function tokenizeHtml(code: string): Token[] {
  const tokens: Token[] = []
  const regex =
    /(<!--[\s\S]*?-->)|(<\/?)([a-zA-Z][\w-]*)|([a-zA-Z-]+)(?==")|("(?:\\[\s\S]|[^"\\])*")|(>|\/>)/g
  let last = 0
  for (const m of code.matchAll(regex)) {
    const idx = m.index ?? 0
    if (idx > last) tokens.push({ type: 'text', value: code.slice(last, idx) })
    if (m[1]) tokens.push({ type: 'comment', value: m[0] })
    else if (m[2]) {
      tokens.push({ type: 'punct', value: m[2] })
      tokens.push({ type: 'tag', value: m[3]! })
    } else if (m[4]) tokens.push({ type: 'property', value: m[0] })
    else if (m[5]) tokens.push({ type: 'string', value: m[0] })
    else if (m[6]) tokens.push({ type: 'punct', value: m[0] })
    last = idx + m[0].length
  }
  if (last < code.length) tokens.push({ type: 'text', value: code.slice(last) })
  return tokens
}

/** Normalize an input language name to our canonical set. */
export function resolveLanguage(input?: string): Language {
  if (!input) return 'text'
  const lang = input.toLowerCase()
  if (['typescript', 'ts', 'tsx'].includes(lang)) return lang.endsWith('x') ? 'tsx' : 'ts'
  if (['javascript', 'js', 'jsx'].includes(lang)) return lang.endsWith('x') ? 'jsx' : 'js'
  if (lang === 'json') return 'json'
  if (lang === 'css' || lang === 'scss' || lang === 'less') return 'css'
  if (lang === 'bash' || lang === 'sh' || lang === 'shell' || lang === 'zsh') return 'bash'
  if (lang === 'html' || lang === 'xml' || lang === 'svg') return 'html'
  return 'text'
}
